import { Injectable, signal } from '@angular/core';
import { BehaviorSubject, Observable, Subject, filter, take } from 'rxjs';
import { AdsService } from '../ads/ads.service';

declare const behamics: any;

@Injectable({
  providedIn: 'root',
})
export class SidenavService {
  constructor(private adsService: AdsService) {}

  public globalControlRate = signal<number>(0);

  /** A target another page asked the sidebar to select, by id, until it has. */
  private targetSwitch = new BehaviorSubject<string | null>(null);
  readonly targetSwitch$ = this.targetSwitch.asObservable();
  private targetSwitched = new Subject<{ target: string; switched: boolean }>();

  /**
   * Selects `target` in the sidebar as if the user had picked it. Emits once
   * the sidebar has its target list: true when it switched (or the target was
   * already selected), false when the user has no such target.
   */
  switchTarget(target: string): Observable<boolean> {
    return new Observable<boolean>((subscriber) => {
      const result = this.targetSwitched
        .pipe(
          filter((r) => r.target === target),
          take(1),
        )
        .subscribe((r) => {
          subscriber.next(r.switched);
          subscriber.complete();
        });
      // after subscribing: the sidebar may answer right away
      this.targetSwitch.next(target);
      return result;
    });
  }

  get pendingTargetSwitch(): string | null {
    return this.targetSwitch.value;
  }

  completeTargetSwitch(target: string, switched: boolean): void {
    if (this.targetSwitch.value === target) this.targetSwitch.next(null);
    this.targetSwitched.next({ target, switched });
  }

  private minDate = new BehaviorSubject('');

  private activeTabIndex = new BehaviorSubject(0);

  private seoProject = new BehaviorSubject('');

  private adsProject = new BehaviorSubject('');
  private adsProjects = new BehaviorSubject([]);
  private isAdsProjectsLoading = new BehaviorSubject(true);
  private adsProjectStatus = new BehaviorSubject({
    isLoading: true,
    percentage: 0,
  });

  private isGSCConnected = new BehaviorSubject(false);

  private sidenavComponent = new BehaviorSubject('');

  private isTargetLoading = new BehaviorSubject(false);

  private invoicesStatus = new BehaviorSubject(null);
  private invoiceTotalsObj = null;
  private invoicesFilterStatus = new BehaviorSubject('all');

  private compareDates = new BehaviorSubject({
    isActive: false,
    startDate: new Date(new Date().setHours(0, 0, 0)),
    endDate: new Date(new Date().setHours(new Date().getHours(), 0, 0)),
  });

  getCompareDates() {
    return this.compareDates.asObservable();
  }
  setCompareDates(data: any) {
    this.compareDates.next(data);
  }

  getMinDate() {
    return this.minDate.asObservable();
  }

  childData$(): Observable<any> {
    return this.sidenavComponent.asObservable();
  }

  setMinDate(date: string) {
    this.minDate.next(date);
  }

  getActiveTabIndex() {
    if (sessionStorage.getItem('activeTabIndex')) {
      this.activeTabIndex.next(
        parseInt(sessionStorage.getItem('activeTabIndex') || ''),
      );
      this.removeActiveTabIndex();
    }
    return this.activeTabIndex.asObservable();
  }
  updateActiveTabIndex(index: number) {
    sessionStorage.setItem('activeTabIndex', index.toString());
    this.activeTabIndex.next(index);
  }
  removeActiveTabIndex() {
    sessionStorage.removeItem('activeTabIndex');
  }
  getSeoProject() {
    return this.seoProject.asObservable();
  }
  setSeoProject(project: string) {
    sessionStorage.setItem('selectedProject', JSON.stringify(project));
    this.seoProject.next(project);
  }
  sendDataToSidenav(project: any) {
    this.sidenavComponent.next(project);
  }

  getAdsProjectStatus() {
    return this.adsProjectStatus.asObservable();
  }
  setAdsProjectStatus(status: { isLoading: boolean; percentage: number }) {
    this.adsProjectStatus.next(status);
  }
  getAdsProject() {
    return this.adsProject.asObservable();
  }
  setAdsProject(project: string) {
    sessionStorage.setItem('selectedAdsProject', JSON.stringify(project));
    this.adsProject.next(project);
  }
  setAdsProjects(projects: any) {
    sessionStorage.setItem('adsProjects', JSON.stringify(projects));
    this.adsProjects.next(projects);
  }
  getAdsProjects() {
    return this.adsProjects.asObservable();
  }
  getIsAdsProjectsLoading() {
    return this.isAdsProjectsLoading.asObservable();
  }
  setIsAdsProjectsLoading(isLoading: boolean) {
    this.isAdsProjectsLoading.next(isLoading);
  }

  getIsTargetLoading() {
    return this.isTargetLoading.asObservable();
  }
  setIsTargetLoading(isLoading: boolean) {
    this.isTargetLoading.next(isLoading);
  }
  refetchAdsProjects() {
    this.isAdsProjectsLoading.next(true);
    this.getAdsProjectsFunc();
  }

  getIsGSCConnected() {
    return this.isGSCConnected.asObservable();
  }
  setIsGSCConnected(isConnected: boolean) {
    this.isGSCConnected.next(isConnected);
  }

  getAdsProjectsFunc() {
    this.setIsAdsProjectsLoading(true);

    this.adsService.getProjects().subscribe({
      next: (res) => {
        this.setIsAdsProjectsLoading(false);
        this.setAdsProjects(res);
      },
      error: (error) => {
        console.error(error);
        this.setAdsProjects([]);
        this.setIsAdsProjectsLoading(false);
      },
    });
  }

  getInvoiceTotalsObj() {
    return this.invoiceTotalsObj;
  }
  getInvoiceTotals() {
    return this.invoicesStatus.asObservable();
  }
  setInvoiceTotals(data: any) {
    this.invoiceTotalsObj = data;
    this.invoicesStatus.next(data);
  }
  getInvoiceTotalsFilterStatus() {
    return this.invoicesFilterStatus.asObservable();
  }
  setInvoiceTotalsFilterStatus(status: string) {
    this.invoicesFilterStatus.next(status);
  }

  private statusUpdate$ = new Subject<number>();
  statusUpdateObservable = this.statusUpdate$.asObservable();

  updateProjectStatus(status: number) {
    this.statusUpdate$.next(status);
  }

  triggerPageChange() {
    document.body.classList.add('page_change');

    setTimeout(() => {
      document.body.classList.remove('page_change');
    }, 10);
  }
}
